/**
 * Drawer（A案）
 * - open button (data-component="drawer") が aria-controls で対象 drawer を指す
 * - drawer 内の [data-drawer-close]（backdrop / closeボタン）で閉じる
 * - drawer の “パネル外” クリックでも閉じる（= 背景クリック）
 * - Esc で閉じる
 * - body スクロールロック
 *
 * 追加：
 * - Drawer内リンククリックは「閉じてから遷移（①）」にする
 * - Drawer内の “同一ページ内 #リンク” は「閉じてからスクロール＋#更新」
 */

export class Drawer {
  /**
   * @param {HTMLElement} trigger data-component="drawer" の要素（開くボタン）
   */
  constructor(trigger) {
    this.trigger = trigger;

    const drawerId = trigger.getAttribute("aria-controls");
    this.drawer = drawerId ? document.getElementById(drawerId) : null;

    this.panel = this.drawer ? this.drawer.querySelector(".c-drawer__panel") : null;

    this.isOpen = false;

    // フォーカス復元
    this.prevFocus = null;

    // bind
    this.onOpenClick = this.onOpenClick.bind(this);
    this.onDrawerClick = this.onDrawerClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.init();
  }

  init() {
    if (!this.trigger || !this.drawer) return;

    // 初期状態
    this.drawer.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");

    // open
    this.trigger.addEventListener("click", this.onOpenClick);

    // close / link click
    this.drawer.addEventListener("click", this.onDrawerClick);
  }

  onOpenClick(e) {
  e.preventDefault();

  // ★追加：開いているなら閉じる（= ×で閉じる）
  if (this.isOpen) {
    this.close({ restoreFocus: true });
    return;
  }

  this.open();
}

  onDrawerClick(e) {
    // 1) [data-drawer-close] を踏んだら閉じる（backdrop/closeボタン等）
    const closeEl = e.target.closest("[data-drawer-close]");
    if (closeEl) {
      e.preventDefault();
      this.close();
      return;
    }

    // 2) Drawer内リンク：閉じてから（同一ページ#ならスクロール）/ それ以外は遷移
    const linkEl = e.target.closest("a[href]");
    if (linkEl && this.drawer.contains(linkEl)) {
      // opt-out（将来「閉じずに遷移」したいリンクが出たらこれを付ける）
      if (linkEl.hasAttribute("data-drawer-no-close")) return;

      // 修飾キー等（新規タブなど）は遷移は任せつつ、Drawerだけ閉じる
      if (this.isModifiedClick(e) || linkEl.target === "_blank" || linkEl.hasAttribute("download")) {
        this.close({ restoreFocus: false });
        return;
      }

      const href = linkEl.getAttribute("href");
      if (!href) return;

      // URLとして解釈（相対もOK）
      const url = this.toUrl(href);

      // 同一ページ内 #リンク（#sec1 / /page/#sec1 など）
      if (url && this.isSamePageHash(url)) {
        const hash = url.hash; // "#sec1"
        e.preventDefault();

        this.close({
          restoreFocus: false,
          onClosed: () => {
            // 先にURLの#を更新（ジャンプはさせない）
            this.updateHash(hash);
            // その後にオフセット付きでスムーススクロール
            this.scrollToHash(hash);
          },
        });
        return;
      }

      // 通常リンク：閉じアニメ後に遷移
      e.preventDefault();
      this.close({
        restoreFocus: false,
        onClosed: () => {
          window.location.href = href;
        },
      });
      return;
    }

    // 3) パネル外（= 背景）クリックなら閉じる
    if (this.panel) {
      const clickedInsidePanel = e.target.closest(".c-drawer__panel");
      if (!clickedInsidePanel) {
        this.close();
      }
      return;
    }

    // panel が無い場合の保険
    if (e.target === this.drawer) {
      this.close();
    }
  }

  open() {
    if (!this.drawer || this.isOpen) return;

    this.prevFocus = document.activeElement;

    // 表示
    this.drawer.hidden = false;
    this.drawer.classList.add("is-drawer-panel-open");
    this.drawer.classList.add("is-drawer-open"); // 既存classに合わせて
    document.body.classList.add("is-drawer-lock");

    // aria
    this.trigger.setAttribute("aria-expanded", "true");

    // Esc
    document.addEventListener("keydown", this.onKeyDown);

    // 再オープン時にスクロール位置をリセット（PC用スクロールコンテナ）
    const pcContent = this.drawer.querySelector(".c-drawer__pc-content");
    if (pcContent) pcContent.scrollTop = 0;

    // メニュー項目の遅延（liごと）をJSでセット（個数が増えてもOK）
    this.setItemDelays();

    // フォーカス（close ボタン優先→なければ最初のフォーカス可能要素）
    const focusTarget =
    this.drawer.querySelector(".c-drawer__panel [data-drawer-close]") ||
    this.drawer.querySelector(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

    if (focusTarget) focusTarget.focus();

    this.isOpen = true;
  }

  /**
   * @param {{restoreFocus?: boolean, onClosed?: Function}} [options]
   */
  close(options = {}) {
    if (!this.drawer || !this.isOpen) return;

    const { restoreFocus = true, onClosed } = options;

    this.drawer.classList.remove("is-drawer-panel-open");
    this.drawer.classList.remove("is-drawer-open");

    this.trigger.setAttribute("aria-expanded", "false");

    document.removeEventListener("keydown", this.onKeyDown);

    // isOpen を先に false にしてから setTimeout に入る
    // （閉じる途中に再オープンされた場合の guard のため）
    this.isOpen = false;

    const durationMs = this.getDurationMs();
    const savedFocus = this.prevFocus;

    // アニメーション完了後に hidden・スクロールロック解除・フォーカス復元
    window.setTimeout(() => {
      if (this.isOpen) return; // 閉じる途中に再度開かれた場合はスキップ
      this.drawer.hidden = true;
      document.body.classList.remove("is-drawer-lock");
      if (restoreFocus && savedFocus && typeof savedFocus.focus === "function") {
        savedFocus.focus();
      }
      if (typeof onClosed === "function") onClosed();
    }, durationMs);
  }

  onKeyDown(e) {
    if (e.key === "Escape") this.close();
  }

  // ----------------------------
  // helpers: duration / delays
  // ----------------------------

  /**
   * CSS変数 --pb-drawer-duration（例: 420ms / 0.42s）を読んでms化
   * 無ければ 260ms
   */
  getDurationMs() {
    if (!this.drawer) return 260;

    const v = getComputedStyle(this.drawer).getPropertyValue("--pb-drawer-duration").trim();
    if (!v) return 260;

    if (v.endsWith("ms")) {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : 260;
    }
    if (v.endsWith("s")) {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n * 1000 : 260;
    }
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 260;
  }

  /**
   * メニュー項目の遅延を li ごとに付与（項目数が増えても対応）
   * CSS側：animation-delay: var(--pb-drawer-item-delay, 0ms);
   */
  setItemDelays() {
    if (!this.drawer) return;

    const listItems = this.drawer.querySelectorAll(".l-global-nav--drawer .c-global-nav__list > li");
    if (!listItems.length) return;

    const panelDelay = this.readMsVar("--pb-drawer-items-delay", 120);
    const stagger = this.readMsVar("--pb-drawer-stagger", 90);

    listItems.forEach((li, i) => {
      li.style.setProperty("--pb-drawer-item-delay", `${panelDelay + i * stagger}ms`);
    });
  }

  /**
   * CSS変数をmsとして読む（ms/s両対応）
   */
  readMsVar(name, fallbackMs) {
    if (!this.drawer) return fallbackMs;

    const v = getComputedStyle(this.drawer).getPropertyValue(name).trim();
    if (!v) return fallbackMs;

    if (v.endsWith("ms")) {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : fallbackMs;
    }
    if (v.endsWith("s")) {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n * 1000 : fallbackMs;
    }
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : fallbackMs;
  }

  // ----------------------------
  // helpers: link / hash scroll
  // ----------------------------

  toUrl(href) {
    try {
      return new URL(href, window.location.href);
    } catch {
      return null;
    }
  }

  isSamePageHash(url) {
    if (!url.hash) return false;
    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search
    );
  }

  updateHash(hash) {
    try {
      history.pushState(null, "", hash);
    } catch {
      // fallback
      window.location.hash = hash;
    }
  }

  scrollToHash(hash) {
    if (!hash || hash === "#") return;

    const id = decodeURIComponent(hash.slice(1));
    if (!id) return;

    const target = document.getElementById(id) || document.querySelector(this.safeHashSelector(hash));
    if (!target) return;

    const offset = this.getScrollOffset();
    const top = Math.max(0, target.getBoundingClientRect().top + window.pageYOffset - offset);

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({
      top,
      behavior: reduce ? "auto" : "smooth",
    });

    // アクセシビリティ：スクロール後にフォーカスも移す（必要なら）
    this.focusTarget(target);
  }

  safeHashSelector(hash) {
    // "#sec:1" みたいな時でも壊れないように
    const raw = hash.startsWith("#") ? hash.slice(1) : hash;
    const esc = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(raw) : raw.replace(/([ !"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, "\\$1");
    return `#${esc}`;
  }

  getScrollOffset() {
    // 優先：html[data-scroll-offset] → 次：CSS var --pb-header-h → 最後：0
    const html = document.documentElement;

    const ds = html.getAttribute("data-scroll-offset");
    const dsNum = ds ? Number.parseFloat(ds) : NaN;

    const cssVar = getComputedStyle(html).getPropertyValue("--pb-header-h").trim(); // "64px" 等
    const cssNum = cssVar.endsWith("px") ? Number.parseFloat(cssVar) : Number.parseFloat(cssVar);

    if (Number.isFinite(dsNum)) return dsNum;
    if (Number.isFinite(cssNum)) return cssNum;

    return 0;
  }

  focusTarget(el) {
    // すでにフォーカス可能ならそのまま、無理なら一時的に tabindex を付ける
    const focusable =
      el.matches &&
      el.matches(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

    if (focusable) {
      try { el.focus({ preventScroll: true }); } catch { el.focus(); }
      return;
    }

    const prev = el.getAttribute("tabindex");
    el.setAttribute("tabindex", "-1");
    try { el.focus({ preventScroll: true }); } catch { el.focus(); }

    // 元に戻す（元々tabindexが無ければ削除）
    if (prev === null) {
      el.addEventListener(
        "blur",
        () => {
          el.removeAttribute("tabindex");
        },
        { once: true }
      );
    } else {
      el.setAttribute("tabindex", prev);
    }
  }

  /**
   * Ctrl/Command/Shift/Alt などの「通常遷移ではないクリック」判定
   */
  isModifiedClick(e) {
    return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
  }
}

/**
 * Vite/registry 用：名前付きexport
 */
export function createDrawer(trigger) {
  return new Drawer(trigger);
}
